import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Field, FieldArray, formValueSelector } from 'redux-form';
import { TabPane, Alert } from 'reactstrap';
import { useHasRights } from 'hooks';
import Repeat from 'shared/repeat';
import PermissionDenied from 'shared/permissionDenied';
import {
    Divider,
    Layout,
    Typography,
    CheckboxReduxFormWrapper,
    Button,
    TextFieldReduxFormWrapper,
    DatePickerReduxFormWrapper,
    ColorPickerDialog
} from '@hub-mono/ui';
import styled from 'styled-components';
import { normalizeDate } from '../../../../utils/formUtil';
import { FORM_NAME } from './modal';
import { useSelector } from 'react-redux';

const StyledLayout = styled(Layout)`
    padding: 1.5rem 0.6rem 1.5rem;
`;

const MilestoneRow = React.memo(({ index, milestone, onFieldChange, fields }) => {
    const milestones = useSelector(state => formValueSelector(FORM_NAME)(state, 'milestones')) || [];

    const handleIntervalChange = useCallback(
        interval => {
            if (interval === 'NONE') {
                onFieldChange({ [`${milestone}.repeatTimes`]: 0 });
            }
        },
        [onFieldChange, milestone]
    );

    return (
        <Layout stack gap="--Spacing-200" key={index}>
            <Layout gap="--Spacing-200" vAlign="center">
                <ColorPickerDialog
                    value={fields.get(index).color}
                    onChange={color => onFieldChange({ [`${milestone}.color`]: color })}
                />
                <Field
                    name={`${milestone}.name`}
                    placeholder="Milestone title"
                    label="Milestone title"
                    component={TextFieldReduxFormWrapper}
                />
                <Field
                    name={`${milestone}.date`}
                    label="Date"
                    component={DatePickerReduxFormWrapper}
                    normalize={normalizeDate}
                />
            </Layout>
            <Repeat
                removeBorder={true}
                wrapperClass="px-0 my-0"
                title="Milestone repeat:"
                inputFieldDisabled={!milestones[index] || 'NONE' === milestones[index]?.interval}
                radioInputName={`${milestone}.interval`}
                textInputName={`${milestone}.repeatTimes`}
                onIntervalChange={handleIntervalChange}
            />
            <Layout hAlign="flex-end">
                <Button variant="text" onClick={() => fields.remove(index)} dataCy="button--remove-milestone">
                    Remove milestone
                </Button>
            </Layout>
        </Layout>
    );
});

const renderMilestones = ({ fields, onFieldChange, meta: { error } }) => {
    return (
        <Layout stack gap={32}>
            {error && <Alert color="danger">{error}</Alert>}
            {fields.map((milestone, index) => (
                <MilestoneRow
                    key={index}
                    milestone={milestone}
                    fields={fields}
                    index={index}
                    onFieldChange={onFieldChange}
                />
            ))}

            <Layout>
                <Button
                    onClick={() =>
                        fields.push({ interval: 'NONE', repeatTimes: 0, date: new Date(), name: '', color: '#349E71' })
                    }
                    dataCy="button--add-milestone"
                >
                    Add a milestone
                </Button>
            </Layout>
        </Layout>
    );
};

const MilestonesTab = ({ tabId, onFieldChange, projectKeyWord, displayDatesInput }) => {
    const { canAddDates, hasSettingProjectMilestones } = useHasRights([
        {
            rights: ['settingProjectDates'],
            rule: 'one',
            name: 'canAddDates',
        },
        {
            rights: ['settingProjectMs'],
            rule: 'one',
            name: 'hasSettingProjectMilestones',
        },
    ]);

    return (
        <TabPane tabId={tabId}>
            <Layout stack gap="--Spacing-200">
                {hasSettingProjectMilestones && (
                    <>
                        <StyledLayout gap="--Spacing-150" stack>
                            <Typography variant="label" size="large">
                                Milestones:
                            </Typography>
                            <FieldArray name="milestones" component={renderMilestones} onFieldChange={onFieldChange} />
                        </StyledLayout>
                        <Divider />
                    </>
                )}
                {canAddDates && (
                    <StyledLayout gap="--Spacing-200" stack>
                        <Layout stack>
                            <Typography variant="label" size="large">
                                {projectKeyWord} Start / End Dates:
                            </Typography>
                            <Layout stack>
                                <Field
                                    type="checkbox"
                                    inline
                                    label="Set Start and End Dates"
                                    name="setStartEndDates"
                                    value={true}
                                    component={CheckboxReduxFormWrapper}
                                />
                                <Typography variant="body" size="small">
                                    Set Start and End dates which will appear on the Scheduler and Reports
                                </Typography>
                            </Layout>
                        </Layout>
                        {displayDatesInput ? (
                            <Layout gap="--Spacing-200">
                                <Field
                                    className="w-100"
                                    name="startDate"
                                    label="Start date"
                                    component={DatePickerReduxFormWrapper}
                                    normalize={normalizeDate}
                                    dataCy="project-modal-dates-start-date"
                                />
                                <Field
                                    name="endDate"
                                    label="End date"
                                    className="ml-2 w-100"
                                    component={DatePickerReduxFormWrapper}
                                    normalize={normalizeDate}
                                    dataCy="project-modal-dates-end-date"
                                />
                            </Layout>
                        ) : null}
                    </StyledLayout>
                )}
                {!hasSettingProjectMilestones && !canAddDates && <PermissionDenied />}
            </Layout>
        </TabPane>
    );
};

MilestonesTab.propTypes = {
    tabId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onFieldChange: PropTypes.func.isRequired,
    projectKeyWord: PropTypes.string,
    displayDatesInput: PropTypes.bool,
};

MilestonesTab.defaultProps = {
    projectKeyWord: 'Project',
    displayDatesInput: false,
};

export default MilestonesTab;
